import { useScopedTranslation } from 'application/localization/useScopedTranslation';
import { PillColor } from 'components/uikit/buttons/pill/PillButton';
import {
  getMultipleValues,
  getSingleValue,
} from 'components/uikit/filters/cascading/cascade';
import {
  CascadeControl,
  CascadeLevel,
  CascadeSelection,
} from 'components/uikit/filters/cascading/types';
import { QueryFactory } from 'services/api';
import {
  SystemVariableType,
  UrlType,
  VariableType,
} from 'services/api/api-client.types';

enum VariablesFilterKey {
  Category = 'category',
  VariableType = 'variableType',
  UrlType = 'urlType',
  SystemVariableType = 'systemVariableType',
}

enum VariableCategory {
  Custom = 'custom',
  System = 'system',
}

type VariablesFilterParams = Pick<
  QueryFactory.VariableQuery.SearchVariableQueryParameters,
  'isSystem' | 'variableType' | 'urlTypes' | 'systemVariableTypes'
>;

/** Translates the selection of the filter into the parameters of the variables search.*/
function toSearchFilterParams(
  selection: CascadeSelection<string>,
): VariablesFilterParams {
  const category = getSingleValue(selection, VariablesFilterKey.Category);
  const urlTypes = getMultipleValues(
    selection,
    VariablesFilterKey.UrlType,
  ) as UrlType[];
  const systemVariableType = getMultipleValues(
    selection,
    VariablesFilterKey.SystemVariableType,
  ) as SystemVariableType[];

  return {
    isSystem:
      category === undefined ? undefined : category === VariableCategory.System,
    variableType: getSingleValue(selection, VariablesFilterKey.VariableType) as
      | VariableType
      | undefined,
    urlTypes: urlTypes.length > 0 ? urlTypes : undefined,
    systemVariableTypes:
      systemVariableType.length > 0 ? systemVariableType : undefined,
  };
}

export const useVariableFilterConfig = () => {
  const i18n = useScopedTranslation(
    'Page.Variables.CascadingVariableFilter.Filters',
  );

  const i18nEmums = useScopedTranslation('Enums');

  const variablesFilterConfig: CascadeLevel<string> = {
    key: VariablesFilterKey.Category,
    title: i18n.t('category'),
    control: CascadeControl.Pills,
    color: PillColor.Accent,
    options: [
      {
        value: VariableCategory.Custom,
        title: i18n.t('custom'),
        next: {
          key: VariablesFilterKey.VariableType,
          title: i18n.t('VariableType.title'),
          control: CascadeControl.Pills,
          color: PillColor.Accent,
          options: [
            {
              value: VariableType.Auto,
              title: i18n.t('auto'),
              next: {
                key: VariablesFilterKey.UrlType,
                title: i18n.t('UrlType.title'),
                control: CascadeControl.Dropdown,
                multiple: true,
                placeholder: i18n.t('UrlType.placeholder'),
                options: Object.values(UrlType).map((urlType) => ({
                  value: urlType,
                  title: i18nEmums.t(`UrlType.${urlType}`),
                })),
              },
            },
            { value: VariableType.Manual, title: i18n.t('manual') },
          ],
        },
      },
      {
        value: VariableCategory.System,
        title: i18n.t('system'),
        next: {
          key: VariablesFilterKey.SystemVariableType,
          title: i18n.t('VariableType.title'),
          control: CascadeControl.Dropdown,
          multiple: true,
          placeholder: i18n.t('VariableType.placeholder'),
          options: Object.values(SystemVariableType).map((variableType) => ({
            value: variableType,
            title: i18nEmums.t(`SystemVariableType.${variableType}`),
          })),
        },
      },
    ],
  };

  return {
    variablesFilterConfig,
    toSearchFilterParams,
  };
};
